import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import ReportExportButton from '../../components/ui/ReportExportButton';
import FinancialScopeNotice from '../../components/ui/FinancialScopeNotice';
import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import ProtectedRoute from "../Components/ProtectedRoute";
import {
  Download,
  Search,
  Printer,
  Landmark,
  Wallet,
  BadgeDollarSign,
} from "lucide-react";
import AccountModal from "./AccountModal";

export default function BalanceSheet() {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  const [report, setReport] = useState({
    assets: [],
    liabilities: [],
    equity: [],
    total_assets: 0,
    total_liabilities: 0,
    total_equity: 0,
    balanced: false,
  });

  useEffect(() => {
    loadBalanceSheet();
  }, []);

  async function loadBalanceSheet() {
    const res = await fetch("/api/balance-sheet", {
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      setReport(data);
    }
  }

  const filterAccounts = (list) =>
    list.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));

  const assets = filterAccounts(report.assets);
  const liabilities = filterAccounts(report.liabilities);
  const equity = filterAccounts(report.equity);

  const totalAssets = report.total_assets;
  const totalLiabilities = report.total_liabilities;
  const totalEquity = report.total_equity;

  const balanced = report.balanced;

  return (
    <ProtectedRoute permission="accounting.balance_sheet">
      <DashboardLayout>
      <FinancialScopeNotice/>
        <div className="space-y-8">
          {/* Header */}

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-serif">Balance Sheet</h1>

              <p className="text-sm text-gray-400 mt-1">
                Financial Position Statement
              </p>
            </div>

            <div className="flex gap-3">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-3 text-gray-500"
                />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search account..."
                  className="pl-9 pr-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm"
                />
              </div>

              <PermissionButton permissionModule="accounts" className="px-5 py-2 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2">
                <Printer size={16} />
                Print
              </PermissionButton>

              <ReportExportButton viewPermission="accounting.balance_sheet" className="px-5 py-2 rounded-xl bg-amber-400 text-black flex items-center gap-2">
                <Download size={16} />
                Export
              </ReportExportButton>
            </div>
          </div>

          {/* KPI */}

          <div className="grid md:grid-cols-4 gap-6">
            <KpiCard
              title="Total Assets"
              value={totalAssets}
              color="text-emerald-400"
              icon={Landmark}
            />

            <KpiCard
              title="Liabilities"
              value={totalLiabilities}
              color="text-rose-400"
              icon={Wallet}
            />

            <KpiCard
              title="Equity"
              value={totalEquity}
              color="text-indigo-400"
              icon={BadgeDollarSign}
            />

            <KpiCard
              title="Net Worth"
              value={totalEquity}
              color="text-amber-400"
              icon={Landmark}
            />
          </div>

          {/* Tables */}

          <div className="grid xl:grid-cols-3 gap-6">
            <AccountTable
              title="Assets"
              color="text-emerald-400"
              rows={assets}
              total={totalAssets}
              onSelect={setSelected}
            />

            <AccountTable
              title="Liabilities"
              color="text-rose-400"
              rows={liabilities}
              total={totalLiabilities}
              onSelect={setSelected}
            />

            <AccountTable
              title="Equity"
              color="text-indigo-400"
              rows={equity}
              total={totalEquity}
              onSelect={setSelected}
            />
          </div>

          {/* Balance */}

          <div
            className={`rounded-2xl p-8 border ${
              balanced
                ? "bg-emerald-500/10 border-emerald-500/30"
                : "bg-red-500/10 border-red-500/30"
            }`}
          >
            <h2 className="text-3xl font-serif">Balance Validation</h2>

            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div>
                <p className="text-sm text-gray-400">Assets</p>

                <h3 className="text-4xl mt-2 font-serif text-emerald-400">
                  ₹{totalAssets.toLocaleString()}
                </h3>
              </div>

              <div>
                <p className="text-sm text-gray-400">Liabilities + Equity</p>

                <h3 className="text-4xl mt-2 font-serif text-indigo-400">
                  ₹{(totalLiabilities + totalEquity).toLocaleString()}
                </h3>
              </div>
            </div>

            <div
              className={`mt-8 text-lg font-medium ${
                balanced ? "text-emerald-400" : "text-red-400"
              }`}
            >
              {balanced
                ? "✔ Balance Sheet Balanced"
                : "✖ Balance Sheet Not Balanced"}
            </div>
          </div>
        </div>

        {selected && (
          <AccountModal account={selected} onClose={() => setSelected(null)} />
        )}
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function AccountTable({ title, rows, total, color, onSelect }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
      <div className="px-6 py-4 border-b border-white/10">
        <h2 className="font-serif text-lg">{title}</h2>
      </div>

      <PaginatedTable className="w-full">
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onSelect(row)}
              className="border-t border-white/10 hover:bg-white/5 cursor-pointer transition"
            >
              <td className="px-6 py-4">{row.name}</td>

              <td className={`px-6 py-4 text-right ${color}`}>
                ₹{Number(row.amount).toLocaleString("en-IN")}
              </td>
            </tr>
          ))}

          <tr className="border-t border-white/10 font-semibold">
            <td className="px-6 py-4">Total</td>

            <td className={`px-6 py-4 text-right ${color}`}>
              ₹{total.toLocaleString()}
            </td>
          </tr>
        </tbody>
      </PaginatedTable>
    </div>
  );
}

function KpiCard({ title, value, color, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <div className="flex justify-between items-center">
        <p className="text-xs uppercase tracking-widest text-gray-400">
          {title}
        </p>

        <Icon size={18} />
      </div>

      <h2 className={`mt-5 text-3xl font-serif ${color}`}>
        ₹{value.toLocaleString()}
      </h2>
    </div>
  );
}
